import React, { useEffect, useRef } from 'react';
import { CandlestickData, ISeriesApi } from 'lightweight-charts';
import { useTheme } from '../contexts/ThemeContext';

interface ChartCandleCountdownProps {
  series: ISeriesApi<any>;
  /** The chart's bars, kept out of React state so a tick cannot re-render the chart. */
  candlesRef: React.MutableRefObject<CandlestickData[]>;
}

/** Used until the price scale reports its real width, and if it ever refuses to. */
const FALLBACK_AXIS_WIDTH = 64;

/** Height of the chart's time axis, which the countdown must not fall behind. */
const TIME_AXIS_HEIGHT = 28;

/**
 * The chart draws its own last-price label centred on the live price. Half its height clears
 * the countdown of it, so the two read as one stack on the axis rather than overlapping.
 */
const PRICE_LABEL_HALF_HEIGHT = 10;

/** The countdown's own height, so it can be kept clear of the time axis. */
const CHIP_HEIGHT = 15;

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Longer than a day and the seconds are noise, shorter than an hour and the hours are; the
 * figure shown is always the part that is actually moving.
 */
const formatRemaining = (milliseconds: number): string => {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600) % 24;
  const days = Math.floor(total / 86400);

  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}`;
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
};

/**
 * How long the bar being drawn has left, shown on the price axis under the live price.
 *
 * The length of a bar is taken from the gap between the last two the chart holds rather than
 * from the selected timeframe. The two agree, and reading it from the data means this does not
 * need its own copy of the timeframe-to-duration table — one that would have to be kept in step
 * with the chart's, and would be wrong in exactly the cases where it mattered.
 *
 * Position and text are written straight to the DOM on an animation frame. The price it hangs
 * from moves with every tick, so following it through React state would re-render this subtree
 * tens of times a second for a label that changes once.
 */
const ChartCandleCountdown: React.FC<ChartCandleCountdownProps> = ({ series, candlesRef }) => {
  const { theme } = useTheme();
  const hostRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame: number;

    const sync = () => {
      frame = requestAnimationFrame(sync);

      const host = hostRef.current;
      const chip = chipRef.current;
      if (!host || !chip) return;

      const hide = () => {
        chip.style.visibility = 'hidden';
      };

      const candles = candlesRef.current;
      if (candles.length < 2) return hide();

      const current = candles[candles.length - 1];
      const barSeconds = Number(current.time) - Number(candles[candles.length - 2].time);
      if (!(barSeconds > 0)) return hide();

      let y: number | null = null;
      let axisWidth = FALLBACK_AXIS_WIDTH;
      try {
        y = series.priceToCoordinate(current.close);
        axisWidth = series.priceScale().width() || FALLBACK_AXIS_WIDTH;
      } catch {
        // The series can be disposed mid-frame, e.g. on a symbol change.
        return hide();
      }

      if (y === null) return hide();

      const plotBottom = Math.max(0, host.clientHeight - TIME_AXIS_HEIGHT);
      const top = Math.min(
        Math.max(y + PRICE_LABEL_HALF_HEIGHT, 0),
        Math.max(0, plotBottom - CHIP_HEIGHT)
      );

      chip.style.width = `${axisWidth}px`;
      chip.style.transform = `translateY(${top}px)`;
      chip.style.visibility = 'visible';

      const text = formatRemaining((Number(current.time) + barSeconds) * 1000 - Date.now());
      if (chip.textContent !== text) chip.textContent = text;
    };

    frame = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(frame);
  }, [series, candlesRef]);

  const isDark = theme === 'dark';

  return (
    <div ref={hostRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 15 }}>
      <div
        ref={chipRef}
        title="Time left on the current candle"
        className="absolute text-[10px] font-medium text-center rounded-sm tabular-nums"
        style={{
          right: 0,
          top: 0,
          height: `${CHIP_HEIGHT}px`,
          lineHeight: `${CHIP_HEIGHT}px`,
          visibility: 'hidden',
          color: isDark ? '#e5e7eb' : '#374151',
          background: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(243, 244, 246, 0.9)'
        }}
      />
    </div>
  );
};

export default ChartCandleCountdown;
